import React, { createContext, useContext, useState, useCallback } from 'react';
import { Snackbar, Alert, AlertColor } from '@mui/material';

interface ToastProps {
  title?: string;
  description?: string;
  variant?: AlertColor;
}

interface ToastState extends ToastProps {
  open: boolean;
}

interface ToastContextProps {
  showToast: (toast: ToastProps) => void;
}

const ToastContext = createContext<ToastContextProps | undefined>(undefined);

export const useToastContext = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToastContext must be used within a ToastProvider");
  }
  return context;
};

interface ToastProviderProps {
  children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
  const [toastState, setToastState] = useState<ToastState>({
    open: false,
    title: '',
    description: '',
    variant: 'success',
  });

  const showToast = useCallback(({ title, description, variant = "success" }: ToastProps) => {
    setToastState({
      open: true,
      title,
      description,
      variant,
    });
  }, []);

  const dismissToast = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setToastState((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={toastState.open}
        autoHideDuration={3000} // Toast will disappear after 3 seconds
        onClose={dismissToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={dismissToast} severity={toastState.variant} sx={{ width: "100%" }}>
          {toastState.title && <div className="font-semibold">{toastState.title}</div>}
          {toastState.description}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};